import type { AnswersJson, Contest, PVExtracted, User } from '@anuleaza/shared';
import { ApiHttpError } from './query.js';

// Base URL is empty in dev — Vite proxies /api → http://localhost:8787.
// In prod, the SPA at anuleaza.ro talks to api.anuleaza.ro and we set this to
// that origin at build time via VITE_API_URL.
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export interface RequestOpts extends RequestInit {
  json?: unknown;
}

export async function apiFetch<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { json, headers, ...rest } = opts;
  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
  };
  if (json !== undefined) init.body = JSON.stringify(json);

  const res = await fetch(`${API_BASE}${path}`, init);
  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const message =
      (isJson && (body as { message?: string }).message) ||
      `HTTP ${res.status}`;
    const code = (isJson && (body as { error?: string }).error) || 'unknown';
    throw new ApiHttpError(res.status, code, message);
  }
  return body as T;
}

// --- typed endpoints ---

export const api = {
  me: () => apiFetch<{ user: User; stats: { pe_rol: number; castigate: number; pierdute: number; total_economisit: number } }>('/api/me'),
  myContests: () => apiFetch<{ contests: Contest[] }>('/api/me/contests'),
  contest: (id: string) => apiFetch<{ contest: Contest }>(`/api/contests/${id}`),
  contestEvents: (id: string) =>
    apiFetch<{ events: { id: string; type: string; data: Record<string, unknown> | null; created_at: string }[] }>(
      `/api/contests/${id}/events`,
    ),
  createContest: (body: { pv_image_key?: string }) =>
    apiFetch<{ contest: Contest }>('/api/contests', { method: 'POST', json: body }),
  uploadPv: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_BASE}/api/upload`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      throw new ApiHttpError(res.status, body.error ?? 'upload_failed', body.message ?? 'Upload eșuat.');
    }
    return (await res.json()) as { key: string; bytes: number; content_type: string };
  },
  extractContest: (id: string) =>
    apiFetch<{ contest: Contest }>(`/api/contests/${id}/extract`, { method: 'POST' }),
  patchExtracted: (id: string, extracted: PVExtracted) =>
    apiFetch<{ contest: Contest }>(`/api/contests/${id}/extracted`, {
      method: 'PATCH',
      json: { extracted },
    }),
  saveAnswers: (id: string, answers: AnswersJson) =>
    apiFetch<{ contest: Contest }>(`/api/contests/${id}/answers`, {
      method: 'POST',
      json: { answers },
    }),
  analyzeContest: (id: string) =>
    apiFetch<{ contest: Contest }>(`/api/contests/${id}/analyze`, { method: 'POST' }),
  generatePlangere: (id: string) =>
    apiFetch<{ contest: Contest }>(`/api/contests/${id}/generate`, { method: 'POST' }),
  pdfUrl: (id: string, variant: 'draft' | 'clean' = 'draft') =>
    `${API_BASE}/api/contests/${id}/pdf?variant=${variant}`,
  joinWaitlist: (body: { email: string; source?: string; turnstile_token?: string }) =>
    apiFetch<{ ok: true; created: boolean }>('/api/waitlist', { method: 'POST', json: body }),
  requestMagicLink: (body: {
    email: string;
    redirect?: string;
    marketing_consent?: boolean;
    turnstile_token?: string;
  }) =>
    apiFetch<{ ok: true; dev_verify_url?: string }>('/api/auth/magic', {
      method: 'POST',
      json: body,
    }),
  logout: () => apiFetch<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
};
